import {
  Module,
  sendNotification,
  useApiClient,
  useAsyncCache,
  useModuleFederationContext,
  useNavigation,
  useTranslation,
  useUrlContext,
  useViewBaseMetadata,
  ViewCustomAction,
  ViewCustomActionArgs,
} from '@msdk/common'
import {
  CustomActionButtonAspect,
  CustomActionDefinition,
  CustomActionPosition,
  Entity,
  EntityMetadata,
  ViewAcl,
  ViewModel,
} from '@msdk/definitions'
import { useEffect, useState } from 'react'

import { ApiAction } from '../components/ViewActions/ApiAction'
import { ViewActionButton } from '../models/form/view-action-button'
import { resolveItem } from '../utils'
import { useComponentModal, useDialogInputModal, useDialogModal, useFormModal, useViewModal } from './modal'

type ActionInstance = {
  action: ViewCustomAction
  definition: CustomActionDefinition
}

type ActionInstanceWithUi = ActionInstance & {
  ui: {
    isDisabled: boolean
    isVisible: boolean
  }
}

const resolveConstructor = (
  definition: CustomActionDefinition,
  module: Module,
  t: (idml: string, description: string) => string,
) => {
  if (definition.api) {
    return ApiAction
  }
  if (module) {
    const ctor = resolveItem(module, definition.fullName)
    if (ctor) {
      return ctor
    }
    sendNotification({
      message: t(
        'CMPSR_SERVICE_CUSTOM_ACTION_VIEW_ERROR',
        'The view is missing some actions and it may not work properly.',
      ),
      status: 'error',
    })
    console.error(`[ViewActions] - custom action not found, ${definition.fullName}`)
  }

  return definition.api ? ApiAction : module ? resolveItem(module, definition.fullName) : undefined
}

const useViewActionsFactory = (
  viewModel: ViewModel,
  position: CustomActionPosition | null,
  reload: () => Promise<void>,
) => {
  const { customActions, entity: entityName, name: viewName } = viewModel

  const t = useTranslation()
  const baseMetadata = useViewBaseMetadata(entityName, viewName) as EntityMetadata
  const modules = useModuleFederationContext()

  const module = modules.loadedModules[baseMetadata.msProvider]
  const [actionInstances, setActionInstances] = useState<ActionInstance[]>([])
  const apiClient = useApiClient()
  const formModal = useFormModal()
  const viewModal = useViewModal()
  const componentModal = useComponentModal()
  const dialogModal = useDialogModal()
  const dialogInputModal = useDialogInputModal()
  const asyncCache = useAsyncCache()
  const { context } = useUrlContext()
  const navigate = useNavigation()

  useEffect(() => {
    const initActions = async () => {
      const instances: ActionInstance[] = []
      const initPromises: Promise<void>[] = []
      const definitions = customActions.filter(({ position: p }) => p === position)

      definitions.forEach((definition) => {
        const args: ViewCustomActionArgs = {
          apiClient,
          modals: { formModal, viewModal, componentModal, dialogModal, dialogInputModal },
          translate: t,
          reload,
          selectedRows: [],
          asyncCache,
          api: definition.api,
          entityName,
          apiRequireClaim: definition.apiRequireClaim,
          urlContext: context,
          navigate,
          config: definition.config,
        }

        const ActionConstructor = resolveConstructor(definition, module, t)

        if (!ActionConstructor) {
          return
        }

        const action = new ActionConstructor(args)

        instances.push({
          action,
          definition,
        })
        initPromises.push(action.onInit())
      })

      await Promise.all(initPromises)

      setActionInstances(instances)
    }
    initActions().catch(console.error)
  }, [viewModel, position, reload])

  return actionInstances
}

const mapInstancesWithUi = (
  instances: ActionInstance[],
  acl?: ViewAcl,
  selectedRows?: Entity[],
): ActionInstanceWithUi[] => {
  instances.forEach((a) => {
    a.action.updateAcl(acl)
    a.action.updateSelectedRows(selectedRows ?? [])
  })
  return instances.map((i) => ({
    ...i,
    ui: {
      isDisabled: i.action.isDisabled(),
      isVisible: i.action.isVisible(),
    },
  }))
}

const useViewActionsWithUi = (instances: ActionInstance[], acl?: ViewAcl, selectedRows?: Entity[]) => {
  const [actionInstances, setActionInstances] = useState<ActionInstanceWithUi[]>(
    mapInstancesWithUi(instances, acl, selectedRows),
  )

  useEffect(() => {
    setActionInstances(mapInstancesWithUi(instances, acl, selectedRows))
  }, [instances, acl, selectedRows])

  return actionInstances.map((i) => ({
    onClick: i.action.onClick.bind(i.action),
    aspect: i.definition.buttonAspect ?? CustomActionButtonAspect.Primary,
    idml: i.definition.idml,
    description: i.definition.description,
    icon: i.definition.icon,
    disabled: i.ui.isDisabled,
    visible: i.ui.isVisible,
    tooltip: i.definition.tooltip,
  }))
}

export function useViewActionButtons(
  viewModel: ViewModel,
  position: CustomActionPosition | null,
  reload: () => Promise<void>,
  selectedRows?: Entity[],
  acl?: ViewAcl,
): ViewActionButton[] {
  const customActionsInstances = useViewActionsFactory(viewModel, position, reload)
  return useViewActionsWithUi(customActionsInstances, acl, selectedRows)
}
